import { parsePhoneNumberFromString, type CountryCode } from 'libphonenumber-js';
import { ref } from 'vue';

export const PRICING_COUNTRY_STORAGE_KEY = 'gabber:pricingCountry';
export const REGIONAL_PRICING_OVERRIDE_STORAGE_KEY = 'gabber:regionalPricingOverride';

// This is deliberately module-scoped: phone pickers can live upstream of a
// checkout panel, so country changes must be observable without a reload.
export const persistedPricingCountry = ref<CountryCode | null>(null);

const normalizeCountry = (value: string | null | undefined): CountryCode | null => {
  const normalized = value?.trim().toUpperCase();
  return normalized && /^[A-Z]{2}$/.test(normalized) ? (normalized as CountryCode) : null;
};

export const getPricingCountry = (): CountryCode | null => {
  if (persistedPricingCountry.value) return persistedPricingCountry.value;
  if (typeof window === 'undefined') return null;
  try {
    persistedPricingCountry.value = normalizeCountry(
      window.localStorage.getItem(PRICING_COUNTRY_STORAGE_KEY)
    );
    return persistedPricingCountry.value;
  } catch {
    return null;
  }
};

/**
 * cas-5cbb: the country a phone number belongs to, for the pricing hint. A number libphonenumber
 * can't place in one region (Ofcom's +447700900xxx drama range, some new ranges) still has a
 * calling code; its main region (+44 → GB) is the honest answer, never "unknown, so US".
 */
export const pricingCountryForPhone = (phone: string | null | undefined): CountryCode | null => {
  const parsed = phone ? parsePhoneNumberFromString(phone) : undefined;
  return parsed?.country ?? parsed?.getPossibleCountries()[0] ?? null;
};

export const persistPricingCountry = (country: string | null | undefined): CountryCode | null => {
  const normalized = normalizeCountry(country);
  persistedPricingCountry.value = normalized;
  if (typeof window === 'undefined' || !normalized) return normalized;
  try {
    window.localStorage.setItem(PRICING_COUNTRY_STORAGE_KEY, normalized);
  } catch {
    /* non-fatal */
  }
  return normalized;
};

export interface PricingMarketWorkspace {
  pricingCountry?: string | null;
}

/**
 * The one client-side market context used by plans and checkout. An authenticated
 * workspace is authoritative; local storage is only a pre-account hint.
 */
export const getPricingMarketContext = (workspace?: PricingMarketWorkspace | null) => {
  persistRegionalPricingOverrideFromUrl();
  return {
    pricingCountry: normalizeCountry(workspace?.pricingCountry) ?? getPricingCountry() ?? undefined,
    regionalPricing: getRegionalPricingOverride() || undefined
  };
};

export const getRegionalPricingOverride = (): boolean => {
  if (typeof window === 'undefined') return false;
  try {
    return window.localStorage.getItem(REGIONAL_PRICING_OVERRIDE_STORAGE_KEY) === '1';
  } catch {
    return false;
  }
};

export const persistRegionalPricingOverrideFromUrl = (): boolean => {
  if (typeof window === 'undefined') return false;
  const enabled = new URLSearchParams(window.location.search).get('regional_pricing') === 'true';
  if (!enabled) return getRegionalPricingOverride();
  try {
    window.localStorage.setItem(REGIONAL_PRICING_OVERRIDE_STORAGE_KEY, '1');
  } catch {
    /* URL still applies now */
  }
  return true;
};
